import { defineStore } from "pinia";
import { ref, readonly, computed } from "vue";
import { auth, db, functions } from "@/mixins/firebase";
import {
  User,
  IdTokenResult,
  ParsedToken,
  signInWithEmailAndPassword,
  getAuth,
  signInWithCustomToken,
  updateProfile,
  reauthenticateWithCredential,
  EmailAuthProvider,
  updatePassword,
} from "firebase/auth";
import * as Sentry from "@sentry/vue";
import { sentryClient } from "@/sentry";
import { formatError } from "@/mixins/errorFormatter";
import { httpsCallable } from "firebase/functions";
import { doc, getDoc, onSnapshot, updateDoc } from "firebase/firestore";
import { useEntityStore } from "./entity";

export type Role = "user" | "super-user" | "staff";

type CustomClaimsPasscodeLogin = {
  role: "technician";
  passcodeAuth: true;
  passcodeId: string;
  provisionedFor: string;
  expiresAt: Date | null;
  sessionExpiresAt: number;
  active: boolean;
};

type CustomClaimsRegularLogin = {
  role: Role;
  entityId?: string;
  configId?: string | null;
  mimboxVersions?: ("MB2" | "MB1")[];
};

type CustomClaimsParsedTokenRegularLogin = ParsedToken & CustomClaimsRegularLogin;
type CustomClaimsParsedTokenPasscodeLogin = ParsedToken & CustomClaimsPasscodeLogin;

type CustomIdTokenResult = IdTokenResult & { claims: CustomClaimsParsedTokenRegularLogin };
type CustomIdTokenResultPasscodeLogin = IdTokenResult & {
  claims: CustomClaimsParsedTokenPasscodeLogin;
};

/**
 * Auth Store - Centralized authentication state management
 *
 * Manages Firebase authentication including email/password login and passcode
 * authentication for technician access via QR codes.
 *
 * IMPORTANT: Always use the `is.` methods to check authentication status.
 * Do NOT check `user` object truthiness directly, as passcode login also
 * populates the user object but may not have full account access.
 *
 * CORRECT usage:
 *   const authStore = useAuthStore();
 *   if (!authStore.is.staff()) { return; }
 *
 * INCORRECT usage:
 *   if (!authStore.user) { return; }  // WRONG - passcode also sets user
 *
 * The `initialized` flag indicates Firebase has completed its initial
 * auth state check. Router guards use this to wait before allowing
 * protected route navigation on page refresh.
 */
export const useAuthStore = defineStore("auth", () => {
  const user = ref<User | null>(null);
  const initialized = ref(false);
  const idToken = ref<CustomIdTokenResult | null>(null);
  const idTokenPasscode = ref<CustomIdTokenResultPasscodeLogin | null>(null);
  const token = ref<string | null>(null);

  const entity = computed(() => {
    const entityStore = useEntityStore();
    return {
      ...entityStore,
      ...entityStore.fullObject,
    };
  });

  const hasAccessToMimboxVersions = computed(() => {
    return idToken.value?.claims.mimboxVersions || [];
  });

  const showRevokedModal = ref(false);
  const revokedReason = ref<string | null>(null);

  let unsubUserDoc: (() => void) | null = null;
  /** `last_seen` from the user doc: `undefined` until the doc has loaded, `null` if never set */
  const lastSeen = ref<Date | null | undefined>(undefined);

  auth.onIdTokenChanged(
    async (firebaseUser) => {
      // routing waits for `initialized`, so it is set no matter how this ends
      try {
        user.value = firebaseUser;

        if (unsubUserDoc) {
          unsubUserDoc();
          unsubUserDoc = null;
        }

        // uid only: the user's email stays out of Sentry
        Sentry.setUser(firebaseUser ? { id: firebaseUser.uid } : null);
        if (!firebaseUser) {
          lastSeen.value = undefined;
          return;
        }

        // not forced: a forced refresh fires onIdTokenChanged again, running this handler twice
        let idTokenResult: Awaited<ReturnType<typeof firebaseUser.getIdTokenResult>> | null = null;
        try {
          idTokenResult = await firebaseUser.getIdTokenResult();
        } catch (e) {
          console.error(e);
        }
        if (!idTokenResult) return;
        extractClaims(idTokenResult);

        if (!isPasscode()) {
          unsubUserDoc = onSnapshot(doc(db, "users", firebaseUser.uid), (snap) => {
            // estimate pending serverTimestamp() writes so our own write is seen immediately
            const data = snap.data({ serverTimestamps: "estimate" });
            lastSeen.value = snap.exists() ? (data?.last_seen?.toDate() ?? null) : undefined;

            const revokedAt = data?.tokensRevokedAt?.toMillis();
            if (!revokedAt || !token.value) return;
            try {
              const payload = JSON.parse(atob(token.value.split(".")[1]));
              if (payload.auth_time * 1000 < revokedAt) {
                showRevokedModal.value = true;
                revokedReason.value = data?.tokensRevokedReason || null;
              }
            } catch {
              // Malformed token, ignore
            }
          });
        }

        try {
          await getEntity(idTokenResult);
        } catch (error) {
          console.error(error);
        }
      } finally {
        initialized.value = true;
      }
    },
    (err) => {
      console.log("err", err);
    },
  );

  function extractClaims(
    newToken: CustomIdTokenResultPasscodeLogin | CustomIdTokenResult | IdTokenResult,
  ) {
    token.value = newToken.token;
    if (newToken.claims?.passcodeAuth) {
      idTokenPasscode.value = newToken as CustomIdTokenResultPasscodeLogin;
      Sentry.setTag("user.role", "passcode");
    } else {
      idToken.value = newToken as CustomIdTokenResult;
      // Lets traces, errors and replays be filtered by role, e.g. staff-only load times
      Sentry.setTag("user.role", idToken.value.claims?.role || "user");
    }
  }

  async function getEntity(
    token: CustomIdTokenResultPasscodeLogin | CustomIdTokenResult | IdTokenResult,
  ) {
    if (token.claims?.passcodeAuth) return;

    const typedT = token as CustomIdTokenResult;
    const entityId = typedT.claims?.entityId;
    if (!entityId) {
      // Drops a previous user's entity (and its listener), which would otherwise go on filtering
      // the config's features, e.g. when staff sign in without a page reload
      entity.value.$reset();
      entity.value.config.removePersistentStorage();
      entity.value.config.reset({
        loaded: true,
      });
      return;
    }

    const currentUid = user.value?.uid;
    const storedUid = localStorage.getItem("conf_user");
    if (currentUid && storedUid && currentUid !== storedUid) {
      entity.value.config.removePersistentStorage();
    }
    const configId = typedT.claims?.configId;
    await entity.value.loadEntity(entityId, configId);
    if (currentUid) {
      localStorage.setItem("conf_user", currentUid);
    }
  }
  /* internal func to check if the current user is a passcode user or not */
  function _isPasscode(): boolean {
    if (!idTokenPasscode.value) return false;

    // The IdTokenPasscode is malformed. Do not trust it.
    if (!idTokenPasscode.value?.claims?.passcodeAuth) {
      return false;
    }
    return true;
  }

  function isPasscode(): boolean {
    if (!idTokenPasscode.value) return false;

    // The IdTokenPasscode is malformed. Do not trust it.
    if (!idTokenPasscode.value?.claims?.passcodeAuth) {
      return false;
    }
    // Session has expired (2 hour limit)
    if (
      idTokenPasscode.value?.claims?.sessionExpiresAt &&
      Date.now() > idTokenPasscode.value.claims.sessionExpiresAt
    ) {
      return false;
    }

    return idTokenPasscode.value.claims.active ?? false;
  }

  function isLoggedIn(): boolean {
    return !!user.value && !_isPasscode();
  }

  /** Loads the signed-in user's entity and config again, e.g. after a QR code page replaced it. */
  async function reloadEntity() {
    if (!isLoggedIn() || !idToken.value) return;
    await getEntity(idToken.value);
  }

  /*
   * Check user roles
   *  - maybe return true in case:
   *    - isUser() is called but user is super-user or staff
   *    - IsSuperUser() is called but user is staff
   * */

  /** Mimbly staff - Has CRUD on almost all resources */
  function isStaff(): boolean {
    return idToken.value?.claims.role === "staff";
  }
  /** super-user - Has CRUD to almost all resources within self entitiy + child entities */
  function isSuperUser(): boolean {
    return idToken.value?.claims.role === "super-user";
  }

  /** user - Can READ almost all resources within self entitiy + child entities */
  function isUser(): boolean {
    return idToken.value?.claims.role === "user";
  }

  function isAnyRole(...roles: Role[]) {
    if (!idToken.value?.claims.role) return false;
    return roles.includes(idToken.value?.claims.role);
  }

  /** This check will most centanly be moved to some organization config for features
   *  Today this is checking for a very specific organization but we need to make it more accessible
   *
   * */
  function isOrganization(organization: "Electrolux Professional"): boolean;
  function isOrganization(organization: string): boolean;
  function isOrganization(organization: string): boolean {
    if (!entity.value?.name) return false;
    if (organization === entity.value.name) {
      return true;
    }

    return managedByOrganization(organization);
  }

  // FIXME: For sub orgs/customes we need to check ancestors if it's within an organization
  function managedByOrganization(organization: "Electrolux Professional"): boolean;
  function managedByOrganization(organization: string): boolean;
  function managedByOrganization(_organization: string): boolean {
    return false;
  }

  async function resetUserPassword(user: User | null, newPassword: string) {
    if (!user || !user.email) return;
    await updatePassword(user, newPassword);
  }

  async function deleteUser(user: User | null) {
    if (!user) return;
    // Users can't delete their own users doc under the rules; the function deletes the doc,
    // the auth account and the user's notification subscriptions
    const deleteUserFunc = httpsCallable<{ uid: string }>(functions, "deleteUser");
    await deleteUserFunc({ uid: user.uid });
  }

  async function updateUser(
    user: User | null,
    fields: {
      name?: string;
    },
  ) {
    if (!user) return;
    const docRef = doc(db, "users", user.uid);
    const snap = await getDoc(docRef);
    if (!snap.exists()) {
      throw new Error(`User ${user.uid} does not exists`);
    }
    try {
      await updateDoc(docRef, fields);
    } catch (error) {
      throw new Error("FS Unable to update user");
    }

    try {
      await updateProfile(user, {
        displayName: fields?.name,
      });
    } catch (error) {
      throw new Error("FB Unable to update user");
    }
  }

  async function reauthenticateUser(user: User | null, password: string) {
    if (!user || !user.email) return;
    const credentials = EmailAuthProvider.credential(user.email, password);
    await reauthenticateWithCredential(user, credentials);
  }

  async function login(email: string, password: string): Promise<[boolean, Error | null]> {
    try {
      await signInWithEmailAndPassword(auth, email, password);

      const idTokenResult = await auth.currentUser?.getIdTokenResult(true);
      if (!idTokenResult) {
        await auth.signOut();
        return [false, new Error("Login failed")];
      }

      user.value = auth.currentUser;
      extractClaims(idTokenResult);
      return [true, null];
    } catch (error) {
      if (sentryClient) {
        sentryClient?.captureException(error);
      }

      await auth.signOut();
      const { summary } = formatError(error);
      return [false, new Error(summary)];
    }
  }

  async function loginPasscode(passcode: string): Promise<void> {
    const passcodeAuth = httpsCallable<string, string>(functions, "passcodeAuth");
    const { data: token } = await passcodeAuth(passcode);
    const auth = getAuth();
    await signInWithCustomToken(auth, token);
    if (!auth.currentUser) {
      throw new Error("No user after login");
    }
    const idToken = await auth.currentUser.getIdTokenResult();
    extractClaims(idToken);
    if (!isPasscode()) {
      await auth.signOut();
      throw new Error();
    }
  }

  async function confirmRevokedSession() {
    showRevokedModal.value = false;
    revokedReason.value = null;
    logout(window.location.pathname);
  }

  async function logout(redirectTo?: string): Promise<void> {
    try {
      await auth.signOut();
    } catch (error) {
      console.error("Error during Firebase signout:", error);
      if (sentryClient) {
        sentryClient?.captureException(error);
      }
    } finally {
      const query = redirectTo ? `?redirect=${encodeURIComponent(redirectTo)}` : "";
      window.location.href = `/login${query}`;
    }
  }

  const rUser = computed(() => {
    if (!user.value) return user.value;
    return {
      ...user.value,
      delete: deleteUser.bind(this, user.value),
      resetPassword: resetUserPassword.bind(this, user.value),
      update: updateUser.bind(this, user.value),
      reauthenticate: reauthenticateUser.bind(this, user.value),
      role: idToken.value?.claims?.role || "user",
    };
  });
  return {
    token: readonly(token),
    user: readonly(rUser),
    initialized: readonly(initialized),
    idToken: readonly(idToken),
    idTokenPasscode: readonly(idTokenPasscode),
    showRevokedModal: readonly(showRevokedModal),
    revokedReason: readonly(revokedReason),
    lastSeen: readonly(lastSeen),
    hasAccessToMimboxVersions,
    entity,
    login,
    loginPasscode,
    logout,
    reloadEntity,
    confirmRevokedSession,
    is: {
      loggedIn: isLoggedIn,
      anyRole: isAnyRole,
      user: isUser,
      superUser: isSuperUser,
      staff: isStaff,
      passcode: isPasscode,
      organization: isOrganization,
    },
  };
});
